import { Select } from "@mantine/core";
import { useMemo, useState } from "react";

import AddShipsStamp from "../../../../components/pages/Settings/YachtSetUp/ShipsStamps/AddShipsStamp";
import ShipsStampsList from "../../../../components/pages/Settings/YachtSetUp/ShipsStamps/ShipsStampsList";
import Breadcrumbs from "../../../../components/shared/Breadcrumbs/Breadcrumbs";
import { Button } from "../../../../components/shared/Button/Button";
import Loader from "../../../../components/shared/Loader/Loader";
import { useGetApi, useTheme } from "../../../../hooks";
import {
  type RawStamp,
  Stamp,
  type UsersLogbook,
} from "../../../../interfaces/Stamps";
import { useDispatch } from "../../../../redux/hooks";
import { setStampsModalProps } from "../../../../redux/slices/generatorSlice";
import { Flex, PageContainer } from "../../../../styled";
import { getYachtId, groupComboboxItems } from "../../../../utils";
import { WDRLbid } from "./ShipsStamp.utils";
import type { ComboboxItem } from "@mantine/core";

const ShipsStamps = () => {
  const theme = useTheme() as any;
  const dispatch = useDispatch();
  const [selectedLogbook, setSelectedLogbook] = useState("*");

  const { data: logbooks, isLoading } = useGetApi<{ logbooks: UsersLogbook[] }>(
    {
      request: "get-users-logbooks",
      fetchOnInit: true,
    },
  );

  const { data: stamps, refetch: fetchStamps } = useGetApi<{
    list: RawStamp[];
  }>({
    request: "get-stamps",
    fetchOnInit: true,
    body: { params: { yachtid: getYachtId() } },
  });

  const groupedLogbooks = useMemo(() => {
    if (!logbooks) return [];
    const lbs = logbooks?.logbooks.flatMap(logbook => {
      if (logbook.lbtype === "logbook")
        return {
          label: logbook.lbname,
          value: logbook.lbid,
          group: "Logbooks",
        };
      else if (logbook.lbtype === "collection" && logbook.parts)
        return logbook.parts.map(part => ({
          label: part.lbname,
          value: part.lbid,
          group: logbook.lbname,
        }));
      return [];
    });

    lbs.unshift({
      value: WDRLbid,
      label: "Waste Declaration",
      group: "Reports",
    });
    lbs.unshift({
      value: "L10",
      label: "Last 10 Ports",
      group: "Reports",
    });
    lbs.unshift({
      value: "crewListReport",
      label: "Crew List Report",
      group: "Reports",
    });
    lbs.unshift({ label: "All", value: "*", group: "Logbooks" });
    return groupComboboxItems(lbs);
  }, [logbooks]);

  const parsedStamps = useMemo(() => {
    if (!stamps) return [];

    const getMatchedLogbooksNames = (stamp: RawStamp) => {
      const parsed = JSON.parse(stamp.logbooks);
      if (!parsed || !Array.isArray(parsed) || parsed.length === 0)
        return ["All"];

      const names: string[] = [];

      // If "*" is present, show "All Logbooks"
      if (parsed.includes("*")) {
        names.push("All Logbooks");
      }

      // Add all report and logbook names
      parsed.forEach((lbid: string) => {
        if (lbid !== "*") {
          const label = groupedLogbooks.find(lb => lb.value === lbid)?.label;
          if (label) names.push(label);
        }
      });

      return names.length > 0 ? names : ["All"];
    };

    return stamps.list.map(stamp => ({
      ...stamp,
      logbooks: getMatchedLogbooksNames(stamp),
    }));
  }, [stamps, groupedLogbooks]);

  const fetchStampsWithParams = async (logbook = "") => {
    const params: { yachtid: string; logbook?: string } = {
      yachtid: getYachtId(),
    };

    if (logbook && logbook !== "*") params.logbook = logbook;
    await fetchStamps({ params });
  };

  const handleChange = (value: string) => {
    setSelectedLogbook(value);
    if (value && value !== "*") void fetchStampsWithParams(value);
    else void fetchStampsWithParams();
  };
  return (
    <PageContainer>
      <Breadcrumbs />
      <Flex
        justify="end"
        align="end"
        style={{ width: "100%", marginTop: "10px", gap: "30px" }}>
        <Select
          miw={300}
          value={selectedLogbook}
          onChange={handleChange}
          data={groupedLogbooks}
          label={<p style={{ color: theme.colors.main[9] }}>Assigned to</p>}
          searchable
        />
        <Button onClick={() => dispatch(setStampsModalProps({ opened: true }))}>
          Add Stamp
        </Button>
      </Flex>
      {isLoading && <Loader />}
      {stamps && (
        <ShipsStampsList
          stamps={parsedStamps as Stamp[]}
          isLoading={false}
          refetchStamps={async () =>
            await fetchStampsWithParams(selectedLogbook)
          }
        />
      )}
      <AddShipsStamp
        logbooks={groupedLogbooks}
        refetchStamps={fetchStampsWithParams}
      />
    </PageContainer>
  );
};
export default ShipsStamps;